/**
 * Workspace Store
 *
 * 워크스페이스(프로젝트 상위 그룹) 상태 관리 — 백엔드 포트의 WorkspaceRepository를 통한다.
 *
 * 기본 워크스페이스는 사용자마다 하나 있는 행(is_default, bff 0017)이라 이 목록에 다른 행과 같이 들어 있다 —
 * 판별·순서는 app/workspaceFilter 하나. 중복 이름 검사도 그 행을 보통 행으로 포함한다(예약어 없음).
 *
 * 동작 다섯의 결과는 **그 호출의 것**(`StoreResult`)이다 — 호출자는 반환값으로 판정하고, 공용 `error` 칸은 화면 표시용으로만
 * 둔다. 겹친 호출이 `error` 를 덮어도 남의 실패를 내 것으로 읽지 않는다(#710 게이트가 조회에서 잡은 구멍을 다섯 동작 전부에서).
 */

import { create } from 'zustand';
import { getBackend } from '@/app/services/backend';
import type { Project, Workspace } from '@/types/project';
import { useProjectStore, putProject } from '@/app/stores/projectStore';
import { validateName } from '@/domain/namePolicy';
import { defaultWorkspaceOf } from '@/app/workspaceFilter';

/** 한 호출의 결과 — 값 또는 실패 사유. 사유는 비어 있지 않다(빈 메시지의 오류도 실패다) */
export type StoreResult<T> = { ok: true; value: T } | { ok: false; error: string };

/** 실패 사유 — 빈 메시지의 오류도 실패다: ''(falsy) 를 돌려주면 호출자가 «실패 없음» 으로 읽는다. 다섯 동작이 같은 규칙을 쓴다 */
const failureMessage = (error: unknown, fallback: string): string =>
  (error instanceof Error && error.message) || fallback;

interface WorkspaceState {
  workspaces: Workspace[];
  loading: boolean;
  /** 마지막 실패의 사유(화면 표시용) — 판정은 각 동작의 반환값(StoreResult)으로 */
  error: string | null;

  /** 목록 조회 — 성공이면 받은 목록 */
  fetchWorkspaces: () => Promise<StoreResult<Workspace[]>>;
  createWorkspace: (name: string, description?: string) => Promise<StoreResult<Workspace>>;
  renameWorkspace: (id: string, name: string, description?: string) => Promise<StoreResult<Workspace>>;
  deleteWorkspace: (id: string) => Promise<StoreResult<void>>;
  /** 프로젝트 소속 이동(대상 워크스페이스 id). projectStore 목록도 갱신한다 — 성공이면 갱신된 프로젝트 */
  moveProject: (projectId: string, workspaceId: string) => Promise<StoreResult<Project>>;
  clearError: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => {
  /** 실패 — 사유를 화면용 칸에 두고 그 호출의 결과로도 돌려준다. 진행 칸(loading)은 그것을 켠 동작만 끈다 */
  const fail = (
    what: string,
    error: unknown,
    fallback: string,
    { clearLoading = true }: { clearLoading?: boolean } = {},
  ): { ok: false; error: string } => {
    const message = failureMessage(error, fallback);
    set(clearLoading ? { error: message, loading: false } : { error: message });
    console.error(`[WorkspaceStore] ${what} error:`, error);
    return { ok: false, error: message };
  };
  /** 사전검사 거절 — 백엔드에 가기 전의 실패(규칙 문구). 진행 칸은 켜지 않았으므로 건드리지 않는다 */
  const refuse = (message: string): { ok: false; error: string } => {
    set({ error: message });
    return { ok: false, error: message };
  };

  return {
    workspaces: [],
    loading: false,
    error: null,

    fetchWorkspaces: async () => {
      set({ loading: true, error: null });
      try {
        const backend = await getBackend();
        const workspaces = await backend.workspaces.listWorkspaces();
        set({ workspaces, loading: false });
        return { ok: true, value: workspaces };
      } catch (error) {
        return fail('fetchWorkspaces', error, '워크스페이스 목록을 불러오는데 실패했습니다');
      }
    },

    createWorkspace: async (name: string, description?: string) => {
      // 이름 규칙(필수·길이·정규화 중복)은 namePolicy 한 곳 (#282) — 서버도 같은 이름 키로 유일성을 든다(bff 0019); 클라이언트가
      // 먼저 막아 요청 낭비를 줄이고 같은 문구를 보인다. 기본 행도 목록의 한 행이다.
      const problem = validateName('workspace', name, get().workspaces.map((w) => w.name));
      if (problem) return refuse(problem);
      set({ loading: true, error: null });
      try {
        const backend = await getBackend();
        const workspace = await backend.workspaces.createWorkspace(name, description);
        set((state) => ({ workspaces: [...state.workspaces, workspace], loading: false }));
        return { ok: true, value: workspace };
      } catch (error) {
        return fail('createWorkspace', error, '워크스페이스 생성에 실패했습니다');
      }
    },

    renameWorkspace: async (id: string, name: string, description?: string) => {
      // 생성과 같은 이름 규칙 — 자기 자신은 목록에서 빼고 비교 (#282)
      const others = get().workspaces.filter((w) => w.id !== id).map((w) => w.name);
      const problem = validateName('workspace', name, others);
      if (problem) return refuse(problem);
      set({ loading: true, error: null });
      try {
        const backend = await getBackend();
        const updated = await backend.workspaces.updateWorkspace(id, { name, description });
        set((state) => ({
          workspaces: state.workspaces.map((w) => (w.id === id ? updated : w)),
          loading: false,
        }));
        return { ok: true, value: updated };
      } catch (error) {
        return fail('renameWorkspace', error, '워크스페이스 변경에 실패했습니다');
      }
    },

    deleteWorkspace: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const backend = await getBackend();
        const defaultId = defaultWorkspaceOf(get().workspaces)?.id;
        if (!defaultId) throw new Error('기본 워크스페이스를 알 수 없어 지우지 않았습니다 — 목록을 다시 불러오세요');
        await backend.workspaces.deleteWorkspace(id);
        set((state) => ({
          workspaces: state.workspaces.filter((w) => w.id !== id),
          loading: false,
        }));
        // 서버가 소속 프로젝트를 기본 행으로 옮겼으므로 로컬 목록도 같은 규칙으로 정합 유지
        useProjectStore.setState((state) => {
          const projects = state.projects.map((p) => (p.workspace_id === id ? { ...p, workspace_id: defaultId } : p));
          // 열린 프로젝트(본문 사본)도 같은 규칙으로 — 목록 항목은 요약이라 현재 프로젝트의 사본이 될 수 없다.
          // 갱신은 `putProject` 로만 한다 — 직접 쓰면 파생인 `currentModel` 이 딸려 가지 않는다(게이트 리뷰 #547 R8).
          return state.currentProject?.workspace_id === id
            ? putProject({ ...state, projects }, { ...state.currentProject, workspace_id: defaultId })
            : { projects };
        });
        return { ok: true, value: undefined };
      } catch (error) {
        return fail('deleteWorkspace', error, '워크스페이스 삭제에 실패했습니다');
      }
    },

    moveProject: async (projectId: string, workspaceId: string) => {
      // 옮기기는 진행 칸(loading)을 켜지 않으므로 실패해도 끄지 않는다 — 겹친 조회의 진행 표시를 지우지 않게
      set({ error: null });
      try {
        const backend = await getBackend();
        const updated = await backend.workspaces.setProjectWorkspace(projectId, workspaceId);
        useProjectStore.setState((state) => putProject(state, updated));
        return { ok: true, value: updated };
      } catch (error) {
        return fail('moveProject', error, '프로젝트 이동에 실패했습니다', { clearLoading: false });
      }
    },

    clearError: () => set({ error: null }),
  };
});

export default useWorkspaceStore;
